// src/features/tuition/components/online/TutorialView.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Chip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as TimeIcon,
  School as SchoolIcon,
  GetApp as DownloadIcon,
  VideoLibrary as VideoIcon,
  Description as DocumentIcon,
  Image as ImageIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import 'react-quill-new/dist/quill.snow.css';

const TutorialView = ({ 
  tutorial, 
  onBack, 
  onEdit,
  onCompleteActivity,
  loading = false
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [quizAnswer, setQuizAnswer] = useState('');
  const [reflectionText, setReflectionText] = useState('');
  
  // Get a reference to the video element
  const videoRef = React.useRef(null);
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={8}>
        <CircularProgress sx={{ color: THEME_COLORS.primary }} />
      </Box>
    );
  }
  
  if (!tutorial) {
    return (
      <Box textAlign="center" p={4}>
        <Typography color="error">Tutorial not found</Typography>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={onBack}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }
  
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  const handleToggleVideo = () => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setVideoPlaying(!videoPlaying);
    }
  };
  
  const openActivityDialog = (activity) => {
    setCurrentActivity(activity);
    setQuizAnswer('');
    setReflectionText('');
    setActivityDialogOpen(true);
  };
  
  const handleCloseActivityDialog = () => {
    setActivityDialogOpen(false);
    setCurrentActivity(null);
  };
  
  const handleCompleteActivity = () => {
    // Validate input based on activity type
    if (currentActivity.type === 'quiz' && !quizAnswer) {
      return; // Don't proceed if no answer selected
    }
    
    if (currentActivity.type === 'reflection' && !reflectionText.trim()) {
      return; // Don't proceed if no reflection text
    }
    
    // Create completion data with timestamp
    const completionData = {
      activityId: currentActivity.id,
      completedAt: new Date().toISOString(),
      response: currentActivity.type === 'quiz' ? quizAnswer : reflectionText,
      correct: currentActivity.type === 'quiz' ? quizAnswer === currentActivity.correctAnswer : undefined
    };
    
    // Call the completion handler
    onCompleteActivity(tutorial.id, completionData);
    
    // Close the dialog
    handleCloseActivityDialog();
  };
  
  const isActivityCompleted = (activityId) => {
    if (!tutorial.completedActivities) return false;
    return tutorial.completedActivities.some(a => a.activityId === activityId);
  };
  
  const getActivityCompletionData = (activityId) => {
    if (!tutorial.completedActivities) return null;
    return tutorial.completedActivities.find(a => a.activityId === activityId);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight="bold">
            Tutorial
          </Typography>
        </Box>
        
        {/* Title and actions */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="flex-start"
          flexWrap="wrap"
          mb={2}
        >
          <Box flex={1} mr={2}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {tutorial.title}
            </Typography>
            
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
              <Chip 
                icon={<SchoolIcon />}
                label={`Grade ${tutorial.grade}`} 
                size="small"
                sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
              />
              <Chip 
                icon={<TimeIcon />}
                label={`${tutorial.duration} minutes`} 
                size="small"
                sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
              />
              {tutorial.status === 'published' && (
                <Chip 
                  label="Published" 
                  size="small"
                  sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' }}
                />
              )}
            </Box>
            
            <Typography variant="body1" paragraph>
              {tutorial.description}
            </Typography>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => onEdit(tutorial.id)}
            sx={{
              borderColor: THEME_COLORS.primary,
              color: THEME_COLORS.primary,
              '&:hover': {
                borderColor: '#1e1c4e',
                backgroundColor: 'rgba(39, 35, 97, 0.04)'
              }
            }}
          >
            Edit Tutorial
          </Button>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Media Section */}
        <Box mb={4}>
          {tutorial.video ? (
            <Box sx={{ position: 'relative', mb: 2 }}>
              <video
                ref={videoRef}
                src={tutorial.video.url}
                style={{
                  width: '100%',
                  maxHeight: '500px',
                  borderRadius: '8px',
                  backgroundColor: '#000'
                }}
                controls
                onPlay={() => setVideoPlaying(true)}
                onPause={() => setVideoPlaying(false)}
              />
              {!videoPlaying && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={handleToggleVideo}
                >
                  <PlayIcon sx={{ color: 'white', fontSize: 40 }} />
                </Box>
              )}
            </Box>
          ) : tutorial.thumbnail && (
            <Box sx={{ position: 'relative', mb: 2 }}>
              <img
                src={tutorial.thumbnail.url}
                alt={tutorial.title}
                style={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
              />
            </Box>
          )}
        </Box>
        
        {/* Tutorial Content */}
        <Box mb={4}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Tutorial Content
          </Typography>
          <Box 
            className="ql-snow"
            sx={{ 
              '.ql-editor': { padding: 0 }
            }}
          >
            <div 
              className="ql-editor"
              dangerouslySetInnerHTML={{ __html: tutorial.content }}
            />
          </Box>
        </Box>
        
        {/* Supporting Materials */}
        {(tutorial.documents?.length > 0 || tutorial.images?.length > 0) && (
          <Box mb={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Supporting Materials
            </Typography>
            
            {/* Documents */}
            {tutorial.documents?.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Documents
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={2}>
                  {tutorial.documents.map((doc, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{ 
                        p: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        width: '100%',
                        maxWidth: '300px',
                        cursor: 'pointer',
                        '&:hover': { 
                          bgcolor: 'rgba(0,0,0,0.02)',
                          borderColor: THEME_COLORS.primary
                        }
                      }}
                      onClick={() => window.open(doc.url, '_blank')}
                    >
                      <DocumentIcon sx={{ mr: 1, color: THEME_COLORS.primary }} />
                      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                          {doc.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {doc.size ? `${Math.round(doc.size / 1024)} KB` : ''}
                        </Typography>
                      </Box>
                      <DownloadIcon fontSize="small" sx={{ color: THEME_COLORS.primary }} />
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}
            
            {/* Additional Images */}
            {tutorial.images?.length > 0 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Additional Images
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={2}>
                  {tutorial.images.map((img, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: 200,
                        height: 150,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid #e0e0e0',
                        cursor: 'pointer',
                        '&:hover': { 
                          boxShadow: 3,
                          transform: 'scale(1.02)',
                          transition: 'all 0.2s ease-in-out'
                        }
                      }}
                      onClick={() => window.open(img.url, '_blank')}
                    >
                      <img
                        src={img.url}
                        alt={`Image ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
        
        {/* Activities Section */}
        {tutorial.microActivities?.length > 0 && (
          <Box mb={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Learning Activities
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Complete these activities to reinforce your learning and track your progress.
            </Typography>
            
            <Stepper orientation="vertical" activeStep={activeStep}>
              {tutorial.microActivities.map((activity, index) => {
                const isCompleted = isActivityCompleted(activity.id);
                const completionData = getActivityCompletionData(activity.id);
                
                return (
                  <Step key={activity.id} expanded>
                    <StepLabel
                      optional={
                        <Typography variant="caption" color="text.secondary">
                          {activity.type === 'quiz' ? 'Multiple Choice Question' : 
                           activity.type === 'reflection' ? 'Reflection Question' : 
                           'Checkpoint'}
                           {activity.isRequired && ' • Required'}
                        </Typography>
                      }
                      StepIconProps={{
                        icon: isCompleted ? <CheckCircleIcon /> : index + 1,
                        active: activeStep === index,
                        completed: isCompleted
                      }}
                    >
                      <Typography 
                        variant="subtitle1" 
                        fontWeight="medium"
                        color={isCompleted ? '#4CAF50' : 'inherit'}
                      >
                        {activity.title}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2">{activity.description}</Typography>
                        
                        {isCompleted ? (
                          <Box 
                            sx={{ 
                              mt: 2, 
                              p: 2, 
                              borderRadius: 1, 
                              bgcolor: 'rgba(76, 175, 80, 0.08)',
                              border: '1px solid rgba(76, 175, 80, 0.2)'
                            }}
                          >
                            <Typography variant="subtitle2" color="#4CAF50" gutterBottom>
                              Activity Completed
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Completed on {formatDate(completionData?.completedAt)}
                            </Typography>
                            
                            {activity.type === 'quiz' && (
                              <Box mt={1}>
                                <Typography variant="body2">
                                  Your answer: <strong>{completionData?.response}</strong>
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color={completionData?.correct ? '#4CAF50' : '#F44336'}
                                >
                                  {completionData?.correct ? 'Correct!' : `The correct answer is ${activity.correctAnswer}`}
                                </Typography>
                              </Box>
                            )}
                            
                            {activity.type === 'reflection' && completionData?.response && (
                              <Box mt={1}>
                                <Typography variant="body2" color="text.secondary">
                                  Your reflection:
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                  "{completionData.response}"
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() => openActivityDialog(activity)}
                            sx={{
                              mt: 2,
                              backgroundColor: THEME_COLORS.primary,
                              '&:hover': { backgroundColor: '#1e1c4e' }
                            }}
                          >
                            Start Activity
                          </Button>
                        )}
                      </Box>
                      
                      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                        <Button
                          disabled={index === 0}
                          onClick={handleBack}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          sx={{
                            mt: 1,
                            mr: 1,
                            backgroundColor: THEME_COLORS.primary,
                            '&:hover': { backgroundColor: '#1e1c4e' }
                          }}
                          disabled={index === tutorial.microActivities.length - 1}
                        >
                          Next
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        )}
      </Paper>
      
      {/* Activity Dialog */}
      <Dialog
        open={activityDialogOpen}
        onClose={handleCloseActivityDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            {currentActivity?.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {currentActivity?.type === 'quiz' ? 'Multiple Choice Question' : 
             currentActivity?.type === 'reflection' ? 'Reflection Question' : 
             'Checkpoint'} • {currentActivity?.points} points
          </Typography>
        </DialogTitle>
        
        <DialogContent dividers>
          {currentActivity && (
            <>
              <Typography variant="body1" paragraph>
                {currentActivity.description}
              </Typography>
              
              {currentActivity.type === 'quiz' && (
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormLabel component="legend">Select your answer:</FormLabel>
                  <RadioGroup
                    value={quizAnswer}
                    onChange={(e) => setQuizAnswer(e.target.value)}
                  >
                    {currentActivity.options.map((option, index) => (
                      <FormControlLabel
                        key={index}
                        value={option}
                        control={<Radio />}
                        label={option}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
              
              {currentActivity.type === 'reflection' && (
                <TextField
                  fullWidth
                  label="Your Reflection"
                  multiline
                  rows={4}
                  value={reflectionText}
                  onChange={(e) => setReflectionText(e.target.value)}
                  placeholder="Write your thoughts here..."
                  variant="outlined"
                />
              )}
              
              {currentActivity.type === 'checkpoint' && (
                <Typography variant="body2" color="text.secondary">
                  Mark this checkpoint as complete to track your progress.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseActivityDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCompleteActivity}
            disabled={
              (currentActivity?.type === 'quiz' && !quizAnswer) ||
              (currentActivity?.type === 'reflection' && !reflectionText.trim())
            }
            sx={{
              backgroundColor: THEME_COLORS.primary,
              '&:hover': { backgroundColor: '#1e1c4e' }
            }}
          >
            Complete Activity
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TutorialView;